import { PAINTS } from "@frontier/content/season";
import { Container, Graphics, Sprite, Text } from "pixi.js";
import type { Texture } from "pixi.js";
import type { ShipSnapshot } from "@frontier/protocol/contracts";
import type { HullId } from "@frontier/content/definitions";
import { npcAppearance } from "./npc-appearance";
import { NpcTelegraph } from "./npc-telegraph";
import { pilotLabel } from "../pilot-label";
import { HULL_ART } from "../hull-art";
import { interpolateAngle } from "./geometry";
import type { LocalPose } from "./local-movement";
import { ShipSilhouetteGlow } from "./ship-silhouette-glow";
import { LocalShipVitals } from "./local-ship-vitals";
import { EnginePlume } from "./engine-plume";
import { HULL_WEAPON_MOUNTS, weaponMountPosition } from "./hull-weapon-mounts";
import type { Point } from "./geometry";

const JAMMER_RADIUS_RATIO = 0.75;

export interface ShipTextures {
  hulls: Record<HullId, Texture>;
  enemy: Texture;
  enemyHeavy: Texture;
  transport: Texture;
  raider: Texture;
  sniper: Texture;
  minelayer: Texture;
  elite: Texture;
  support: Texture;
  boss: Texture;
  alienScout: Texture;
  alienArtillery: Texture;
  alienDisruptor: Texture;
}

export class ShipView {
  readonly container = new Container();
  readonly body = new Container();
  private readonly flame = new EnginePlume();
  private readonly sprite: Sprite;
  private readonly shield = new Graphics();
  private readonly glow: ShipSilhouetteGlow;
  private readonly health = new Graphics();
  private readonly vitals = new LocalShipVitals();
  private readonly label: Text;
  private readonly telegraph = new NpcTelegraph();
  private readonly jamming = new Graphics();
  private previous: ShipSnapshot;
  private current: ShipSnapshot;
  private receivedAt: number;
  private damagedUntil = 0;
  private selected = false;
  private local = false;
  private neutralSecurity = false;
  private length = 68;

  constructor(
    state: ShipSnapshot,
    private readonly textures: ShipTextures,
    now: number,
  ) {
    this.current = state;
    this.container.label = `ship:${state.id}`;
    this.body.label = "heading";
    this.previous = state;
    this.receivedAt = now;
    this.sprite = new Sprite({
      texture: textures.hulls[state.hullId],
      anchor: 0.5,
    });
    this.sprite.rotation = Math.PI / 2;
    this.sprite.label = "hull";
    this.shield.label = "shield";
    this.glow = new ShipSilhouetteGlow(this.body, this.sprite);
    this.label = new Text({
      text: state.name,
      style: {
        fontFamily: "system-ui, sans-serif",
        fontSize: 13,
        fill: 0x9bb0c2,
        letterSpacing: 1,
      },
    });
    this.label.anchor.set(0.5);
    this.body.addChild(this.flame.container, this.sprite);
    this.container.addChild(
      this.jamming,
      this.telegraph.graphics,
      this.shield,
      this.body,
      this.health,
      this.vitals.graphics,
      this.label,
    );
    this.applyAppearance();
  }

  get state(): ShipSnapshot {
    return this.current;
  }

  get shieldRadius(): number {
    return Math.max(this.length, this.sprite.width) * 0.62;
  }

  weaponMuzzle(ordinal: number, count: number, out: Point): boolean {
    if (this.current.kind !== "pilot" && this.current.kind !== "player") return false;
    return weaponMountPosition(
      HULL_WEAPON_MOUNTS[this.current.hullId], ordinal, count,
      this.sprite.width, this.length, this.container, this.body.rotation, out,
    );
  }

  update(
    state: ShipSnapshot,
    now: number,
    local: boolean,
    selected: boolean,
    neutralSecurity = false,
  ): void {
    if (state.hp < this.current.hp || state.shield < this.current.shield)
      this.damagedUntil = now + 160;
    this.previous = this.current;
    this.current = state;
    this.receivedAt = now;
    this.local = local;
    this.selected = selected;
    this.neutralSecurity = neutralSecurity;
    this.applyAppearance();
  }

  setSelected(selected: boolean): void {
    if (selected === this.selected) return;
    this.selected = selected;
    this.drawStatus();
  }

  render(now: number, presentation?: LocalPose, zoom = 1): void {
    const alpha = this.local
      ? 1
      : Math.min(1, Math.max(0, (now - this.receivedAt) / 100));
    const dx = this.current.x - this.previous.x;
    const dy = this.current.y - this.previous.y;
    const teleported = Math.hypot(dx, dy) > 300;
    this.container.position.set(
      presentation?.x ??
        (teleported ? this.current.x : this.previous.x + dx * alpha),
      presentation?.y ??
        (teleported ? this.current.y : this.previous.y + dy * alpha),
    );
    this.body.rotation =
      presentation?.angle ??
      interpolateAngle(this.previous.angle, this.current.angle, alpha);
    this.sprite.tint = now < this.damagedUntil ? 0xffb4a5 : PAINTS[this.current.paintId ?? "default"].tint;
    this.shield.rotation = now / 6000;
    this.vitals.render(
      this.current,
      this.local,
      Math.max(
        Math.hypot(this.sprite.width, this.sprite.height) / 2,
        this.length * JAMMER_RADIUS_RATIO,
      ),
      zoom,
    );
    this.container.alpha = this.current.dead
      ? 0.3
      : this.current.docked
        ? 0.62
        : 1;
    const moving =
      !this.current.dead &&
      !this.current.docked &&
      (presentation?.moving ??
        (Math.hypot(dx, dy) > 0.2 || this.current.boosting));
    this.flame.render(
      this.length,
      moving,
      presentation?.boosting ?? this.current.boosting,
      this.current.kind === "pirate",
      now,
    );
    this.telegraph.render(this.current, this.container, now, this.receivedAt);
    this.jamming.clear();
    if (
      this.current.jammingRemaining - (now - this.receivedAt) / 1000 > 0 &&
      !this.current.dead &&
      !this.current.docked
    )
      for (let index = 0; index < 3; index++) {
        const angle = now / 900 + (index * Math.PI * 2) / 3;
        this.jamming
          .arc(0, 0, this.length * JAMMER_RADIUS_RATIO, angle, angle + 1.1)
          .stroke({ color: 0xc6a2ff, width: 2, alpha: 0.65 });
      }
  }

  destroy(): void {
    this.glow.destroy();
    this.container.destroy({ children: true });
  }

  private applyAppearance(): void {
    const state = this.current;
    const transport = state.kind === "transport";
    const player = state.kind === "pilot" || state.kind === "player";
    const appearance = npcAppearance(state.archetype);
    this.sprite.texture = player
      ? this.textures.hulls[state.hullId]
      : this.textures[appearance.texture];
    this.length = player ? HULL_ART[state.hullId].length : appearance.length;
    this.sprite.height = this.length;
    this.sprite.scale.x = this.sprite.scale.y;
    this.label.text = this.local
      ? ""
      : player
        ? pilotLabel(state.name, state.id)
        : state.name;
    this.label.y = -this.length * 0.52 - 16;
    this.label.style.fill = transport ? 0xffce79 : player || this.neutralSecurity ? 0x93d5e4 : 0xcc9ca0;
    this.drawStatus();
  }

  private drawStatus(): void {
    const state = this.current;
    this.shield.clear();
    const radius = this.length * 0.62;
    this.glow.sync(
      state.dead || state.docked || (!this.local && !this.selected)
        ? null
        : this.local
          ? 0x56e2e3
          : this.neutralSecurity
            ? 0x76bce6
          : state.kind === "transport"
            ? 0xffce79
            : 0xff6b63,
    );
    if (!this.local && state.shield > 0 && state.maxShield > 0)
      this.shield
        .arc(
          0,
          0,
          radius - 3,
          -Math.PI / 2,
          -Math.PI / 2 +
            Math.PI * 2 * Math.min(1, state.shield / state.maxShield),
        )
        .stroke({ color: 0x59ccff, width: 2, alpha: 0.42 });
    this.health.clear();
    if (this.local || state.dead || state.docked) return;
    const width = state.kind === "transport" ? 90 : 54;
    const y = -this.length * 0.52 - 4;
    this.health
      .roundRect(-width / 2, y, width, 3, 1.5)
      .fill({ color: 0x091522, alpha: 0.9 });
    const fraction = Math.min(
      1,
      Math.max(0, state.hp / Math.max(1, state.maxHp)),
    );
    if (fraction > 0)
      this.health.roundRect(-width / 2, y, width * fraction, 3, 1.5).fill({
        color:
          fraction < 0.3
            ? 0xff665b
            : state.kind === "transport"
              ? 0xffcc75
              : 0xb0cbd2,
      });
  }
}
